import React from 'react';
import { Badge } from './ui/badge';
import { TrendingUp, TrendingDown, Minus, Activity, Wifi } from 'lucide-react';

export const MarketStatusBar = ({ market, isAnalyzing, lastAnalysis }) => {
  const indices = market?.indices || {};
  const isOpen = market?.is_open || false;

  const formatPrice = (price) => {
    if (!price) return '—';
    return price >= 10000 ? `${(price / 1000).toFixed(1)}K` : price.toLocaleString(undefined, { maximumFractionDigits: 1 });
  };

  const IndexChip = ({ name, data }) => {
    const change = data?.change_pct || 0;
    const isUp = change > 0;
    const isDown = change < 0;
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
        <span className="text-xs text-[hsl(var(--muted-foreground))] font-medium">{name}</span>
        <span className="font-mono text-sm tabular-nums font-medium">{formatPrice(data?.price)}</span>
        <span className={`flex items-center gap-0.5 font-mono text-xs tabular-nums font-medium ${
          isUp ? 'text-emerald-400' : isDown ? 'text-red-400' : 'text-[hsl(var(--muted-foreground))]'
        }`}>
          {isUp ? <TrendingUp className="w-3 h-3" /> : isDown ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
          {change >= 0 ? '+' : ''}{change.toFixed(2)}%
        </span>
      </div>
    );
  };

  return (
    <div className="mb-6 p-4 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] relative overflow-hidden noise" data-testid="market-status-bar">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 relative z-10">
        <div className="flex items-center gap-3">
          <Badge
            variant={isOpen ? 'default' : 'secondary'}
            className={`${
              isOpen
                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))]'
            } text-xs font-semibold px-3 py-1`}
            data-testid="market-status-state"
          >
            <span className={`w-2 h-2 rounded-full mr-1.5 inline-block ${isOpen ? 'bg-emerald-400 animate-pulse' : 'bg-[hsl(var(--muted-foreground))]'}`} />
            NYSE {isOpen ? 'OPEN' : 'CLOSED'}
          </Badge>
          {isAnalyzing && (
            <Badge variant="outline" className="text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30 text-xs">
              <Activity className="w-3 h-3 mr-1 animate-pulse" />
              Agents Analyzing
            </Badge>
          )}
          <div className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]" data-testid="market-status-latency">
            <Wifi className="w-3 h-3 text-emerald-400" />
            Live
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="market-status-indices">
          {Object.entries(indices).map(([name, data]) => (
            <IndexChip key={name} name={name} data={data} />
          ))}
        </div>
      </div>
    </div>
  );
};
